/**
 * ManpowerCurveCard
 * 
 * 人力供需曲线组件 - 展示一个月内的人力资源供需情况
 * 使用 WxbChartCard 堆叠柱状图（按班次+操作状态）+ 需求折线图
 */

import React, { useState, useEffect, useMemo } from 'react';
import { WxbAlert, WxbButton, WxbOverlay, WxbEmpty, WxbTooltip, WxbKpiCard, WxbChartShell, WxbChartCard, WxbIcon } from '../wxb-ui';
import type { WxbChartSeriesConfig, WxbChartPoint, WxbChartAnnotation } from '../wxb-ui';
import dayjs, { Dayjs } from 'dayjs';
import { dashboardService } from '../../services/dashboardService';
import { ManpowerCurveData } from '../../types/dashboard';
import {
    deriveShiftColorTokens,
    getDefaultShiftColorTokens,
    getShiftColorTone,
    getShiftToneClass,
    resolveShiftColorFamilyFromTokens,
    ShiftColorToken,
} from '../../utils/shiftColorTokens';
import { readWxbToken } from '../wxb-ui/_internal/colorTokens';
import './DashboardChartCommon.css';
import './ManpowerCurveCard.css';

interface ManpowerCurveCardProps {
    date: Dayjs;
    orgPath: number[];
    shiftId?: number;
    /** 在「需求 > 供给」的日期上叠加红色缺口阴影。默认关闭（保持调度中心原样）。 */
    showGapShading?: boolean;
    /** 隐藏顶部 KPI 数字卡片，只保留曲线。默认关闭（保持调度中心原样）。 */
    hideKpis?: boolean;
}

// 固定堆叠顺序（从下到上）
const CATEGORY_ORDER = [
    '基础班(有操作)',
    '标准日班(有操作)',
    '长白班(有操作)',
    '夜班(有操作)',
    '基础班(待命)',
    '标准日班(待命)',
    '长白班(待命)',
    '夜班(待命)',
];

const getCategoryWeight = (category: string): number => {
    const index = CATEGORY_ORDER.indexOf(category);
    return index >= 0 ? index : 999;
};

const categoryLabel = (shiftName: string, hasOperation: boolean): string =>
    hasOperation ? `${shiftName}(有操作)` : `${shiftName}(待命)`;

const ManpowerCurveCard: React.FC<ManpowerCurveCardProps> = ({
    date,
    orgPath,
    shiftId,
    showGapShading = false,
    hideKpis = false
}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<ManpowerCurveData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const dailyData = useMemo(() => (
        Array.isArray(data?.daily_data) ? (data?.daily_data ?? []) : []
    ), [data?.daily_data]);

    const summary = data?.summary ?? {
        avg_gap: '0',
        max_gap: 0,
        max_gap_date: '',
        sufficiency_rate: 100,
        gap_days: 0,
    };

    // 加载曲线数据
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await dashboardService.getManpowerCurve(
                    date.format('YYYY-MM'),
                    orgPath,
                    shiftId
                );
                setData(res);
                setError(null);
            } catch (error) {
                console.error('Failed to load manpower curve:', error);
                setData(null);
                setError('无法加载人力供需曲线，请稍后重试。');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [date, orgPath, shiftId, reloadKey]);

    // ============== WxbChartCard 数据转换 ==============

    // 收集所有班次分类
    const allCategories = useMemo(() => {
        const cats = new Set<string>();
        dailyData.forEach(d => {
            if (d.shift_breakdown && d.shift_breakdown.length > 0) {
                const sorted = [...d.shift_breakdown].sort((a, b) => {
                    const catA = categoryLabel(a.shift_name, a.has_operation);
                    const catB = categoryLabel(b.shift_name, b.has_operation);
                    return getCategoryWeight(catA) - getCategoryWeight(catB);
                });
                sorted.forEach(sb => {
                    const label = categoryLabel(sb.shift_name, sb.has_operation);
                    cats.add(label);
                });
            } else {
                cats.add('可用人数');
            }
        });
        // Sort by category order
        return Array.from(cats).sort((a, b) => getCategoryWeight(a) - getCategoryWeight(b));
    }, [dailyData]);

    const categoryTokenMap = useMemo(() => {
        const map: Record<string, ShiftColorToken> = {};
        dailyData.forEach((d) => {
            (d.shift_breakdown || []).forEach((sb) => {
                const label = categoryLabel(sb.shift_name, sb.has_operation);
                const defaults = getDefaultShiftColorTokens({
                    shift_code: sb.shift_code,
                    shift_name: sb.shift_name,
                });
                const fallbackFamily = resolveShiftColorFamilyFromTokens(defaults);
                const family = resolveShiftColorFamilyFromTokens({
                    no_operation_color_token: sb.no_operation_color_token,
                    operation_color_token: sb.operation_color_token,
                }, fallbackFamily);
                const tokens = deriveShiftColorTokens(family);
                map[label] = sb.has_operation
                    ? tokens.operation_color_token
                    : tokens.no_operation_color_token;
            });
        });
        return map;
    }, [dailyData]);

    // 生成颜色映射
    const categoryColorMap = useMemo(() => {
        const map: Record<string, string> = {};
        allCategories.forEach(cat => {
            const token = categoryTokenMap[cat] ?? 'neutral-soft';
            map[cat] = readWxbToken(getShiftColorTone(token, 'neutral-soft').chartToken);
        });
        return map;
    }, [allCategories, categoryTokenMap]);

    const categorySwatchClassMap = useMemo(() => {
        const map: Record<string, string> = {};
        allCategories.forEach(cat => {
            const token = categoryTokenMap[cat] ?? 'neutral-soft';
            map[cat] = getShiftToneClass(token, 'neutral-soft', 'dashboard-chart-swatch--shift-');
        });
        return map;
    }, [allCategories, categoryTokenMap]);

    // WxbChartCard series config
    const wxbSeriesConfig = useMemo((): WxbChartSeriesConfig[] => {
        const configs: WxbChartSeriesConfig[] = [];
        // 各班次分类 → bar 系列
        allCategories.forEach(cat => {
            configs.push({
                key: cat,
                label: cat,
                color: categoryColorMap[cat] || readWxbToken('--wx-fg-3'),
                geometry: 'bar',
            });
        });
        const demandColor = readWxbToken('--wx-red-500');
        // 需求折线
        configs.push({
            key: 'demand',
            label: '需求人数',
            color: demandColor,
            geometry: 'line',
            lineWidth: 2,
            showPoints: true,
        });
        return configs;
    }, [allCategories, categoryColorMap]);

    // WxbChartCard points
    const wxbChartPoints = useMemo((): WxbChartPoint[] => {
        const dangerColor = readWxbToken('--wx-red-500');
        return dailyData.map(d => {
            const values: Record<string, number> = { demand: d.demand_count };

            // 填充各班次分类的值
            if (d.shift_breakdown && d.shift_breakdown.length > 0) {
                d.shift_breakdown.forEach(sb => {
                    const label = categoryLabel(sb.shift_name, sb.has_operation);
                    values[label] = (values[label] ?? 0) + sb.count;
                });
            } else {
                if (allCategories.includes('可用人数')) {
                    values['可用人数'] = d.available_count;
                }
            }
            // 确保所有分类都有值
            allCategories.forEach(cat => {
                if (values[cat] === undefined) values[cat] = 0;
            });

            // extra tooltip info
            const extra: Array<{ label: string; value: string; color?: string }> = [];

            // 节假日标签
            if (!d.is_workday) {
                if (d.salary_multiplier === 3) {
                    extra.push({ label: d.holiday_name || '法定假日', value: '3倍工资', color: dangerColor });
                } else if (d.is_weekend) {
                    extra.push({ label: '周末', value: '休息日' });
                } else {
                    extra.push({ label: '休息日', value: '' });
                }
            }

            // 缺口信息
            if (d.gap > 0) {
                extra.push({ label: '缺口', value: `${d.gap}人`, color: dangerColor });
            }

            return {
                label: dayjs(d.date).format('M/D'),
                date: d.date,
                values,
                extra: extra.length > 0 ? extra : undefined,
            };
        });
    }, [dailyData, allCategories]);

    // Annotations: 节假日区域 + 总人数参考线
    const wxbAnnotations = useMemo((): WxbChartAnnotation[] => {
        const anns: WxbChartAnnotation[] = [];
        const dangerColor = readWxbToken('--wx-red-500');
        const quietColor = readWxbToken('--wx-ink');
        const referenceColor = readWxbToken('--wx-fg-4');

        // 节假日区域标记
        dailyData.forEach((d, i) => {
            if (!d.is_workday) {
                const isTripleSalary = d.salary_multiplier === 3;
                anns.push({
                    type: 'region',
                    xStart: i,
                    xEnd: i,
                    color: isTripleSalary ? dangerColor : quietColor,
                    opacity: isTripleSalary ? 0.08 : 0.03,
                });
            }
        });

        // 缺口阴影：需求 > 供给的日期叠加红色区域（仅在 showGapShading 开启时）
        if (showGapShading) {
            dailyData.forEach((d, i) => {
                if (d.gap > 0) {
                    anns.push({
                        type: 'region',
                        xStart: i,
                        xEnd: i,
                        color: dangerColor,
                        opacity: 0.14,
                    });
                }
            });
        }

        // 总人数参考线
        if (data?.total_headcount) {
            anns.push({
                type: 'referenceLine',
                yValue: data.total_headcount,
                label: `总人数: ${data.total_headcount}`,
                color: referenceColor,
                dash: [4, 4],
            });
        }

        return anns;
    }, [dailyData, data?.total_headcount, showGapShading]);

    return (
        <WxbChartShell
            icon={<WxbIcon name="users" size={16} />}
            iconColor="blue"
            title="人力供需曲线"
            subtitle={`${date.format('YYYY年M月')} · 堆叠柱状图 + 需求折线`}
            actions={
                <WxbTooltip title="堆叠柱状图显示各班次可用人数（深色=有操作任务，浅色=待命），红色折线为需求人数">
                    <span className="dashboard-chart-help">
                        <WxbIcon name="info" size={13} />
                    </span>
                </WxbTooltip>
            }
        >
            <WxbOverlay loading={loading}>
                {error && !loading ? (
                    <WxbAlert variant="error" title="人力供需曲线加载失败">
                        <div className="dashboard-chart-error-body">
                            <span className="dashboard-chart-error-text">{error}</span>
                            <WxbButton size="sm" variant="secondary" onClick={() => setReloadKey((k) => k + 1)}>
                                重试
                            </WxbButton>
                        </div>
                    </WxbAlert>
                ) : data && dailyData.length > 0 ? (
                    <>
                        {/* KPI 统计块 */}
                        {!hideKpis && (
                        <div className="dashboard-kpi-grid">
                            <WxbKpiCard
                                title="团队总人数"
                                value={data.total_headcount ?? 0}
                                unit="人"
                            />
                            <WxbKpiCard
                                title="人力充足率"
                                value={summary.sufficiency_rate}
                                unit="%"
                                trend={summary.sufficiency_rate >= 80 ? 'up' : 'down'}
                            />
                            <WxbKpiCard
                                title="平均缺口"
                                value={summary.avg_gap}
                                unit="人/天"
                                trend={Number(summary.avg_gap) > 0 ? 'down' : 'up'}
                            />
                            <WxbKpiCard
                                title="峰值缺口"
                                value={summary.max_gap}
                                unit={summary.max_gap_date ? `人 (${dayjs(summary.max_gap_date).format('M/D')})` : '人'}
                                trend={summary.max_gap > 0 ? 'down' : 'up'}
                            />
                        </div>
                        )}

                        {/* 图例 */}
                        <div className="wxb-cs-legend dashboard-chart-legend">
                            {wxbSeriesConfig.filter(sc => sc.geometry === 'bar').map(sc => (
                                <span key={sc.key} className="wxb-cs-legend-item">
                                    <span className={`wxb-cs-swatch dashboard-chart-swatch ${categorySwatchClassMap[sc.key] || 'dashboard-chart-swatch--neutral'}`} />
                                    {sc.label}
                                </span>
                            ))}
                            <span className="wxb-cs-legend-divider" />
                            {wxbSeriesConfig.filter(sc => sc.geometry !== 'bar').map(sc => (
                                <span key={sc.key} className="wxb-cs-legend-item">
                                    <span className="wxb-cs-swatch dashboard-chart-swatch dashboard-chart-swatch--red" />
                                    {sc.label}
                                </span>
                            ))}
                        </div>

                        <WxbChartCard
                            headless
                            seriesConfig={wxbSeriesConfig}
                            points={wxbChartPoints}
                            annotations={wxbAnnotations}
                            yUnit="人"
                            tooltipFormatter={(v) => `${Math.round(v)} 人`}
                        />
                    </>
                ) : (
                    !loading && <WxbEmpty />
                )}
            </WxbOverlay>
        </WxbChartShell>
    );
};

export default ManpowerCurveCard;
